import React from 'react';
import { Plane, ShieldOff, Clock4, Coins, Globe2, Sparkles, CheckCircle2 } from 'lucide-react';

const About = () => {
  const problems = [
    {
      icon: <Plane size={24} className="text-brand-orange" />,
      bg: 'bg-orange-50',
      title: 'The Travel Problem',
      body: 'Every time a pet owner in Coimbatore plans a trip — for work, family, or a holiday — the biggest stress is not the journey. It is the unanswered question: "Who will take care of my pet?" They call friends, ask relatives, browse random Facebook groups. There is no reliable, centralised way to find a trusted local professional.',
    },
    {
      icon: <ShieldOff size={24} className="text-emergency-red" />,
      bg: 'bg-red-50',
      title: 'The Emergency Problem',
      body: 'When a pet falls sick at 2 AM — vomiting, seizures, injury after an accident — the owner panics and searches Google. They find outdated clinic listings, phones that go unanswered, or clinics that are already closed. A platform that shows real-time vet availability has never existed for South Indian cities. Until now.',
    },
  ];

  const values = [
    { icon: <ShieldOff size={20} className="text-brand-teal" />, label: 'No Middlemen', desc: 'You contact the shop or vet directly. We do not take commissions or route calls.' },
    { icon: <Clock4 size={20} className="text-brand-orange" />, label: 'Speed Is Everything', desc: 'For emergencies, we show available vets first. Every second saved is care delivered sooner.' },
    { icon: <Coins size={20} className="text-green-500" />, label: 'Free for Pet Owners', desc: 'PetCares is completely free for pet owners. We work with partners to sustain the platform.' },
    { icon: <Globe2 size={20} className="text-purple-500" />, label: 'South India First', desc: 'We are hyper-local. Starting with Coimbatore and expanding city by city across South India.' },
  ];

  const trustPoints = [
    'All pet shops personally verified by our ground team before listing',
    'Veterinarians registered with State Veterinary Council — no unverified listings',
    'Average emergency response time under 5 minutes from first contact',
    'Zero platform fees for pet owners — always direct, always free',
    'Bilingual support: Tamil & English across all listed partners',
    'Expanding city-by-city: Coimbatore → Chennai → Kochi → Bengaluru → beyond',
  ];

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-brand-orange font-bold tracking-widest uppercase text-xs mb-3 bg-orange-50 px-4 py-1.5 rounded-full border border-orange-100">
            About PetCares
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-5 leading-tight">
            We Exist to Solve <br/>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-orange to-brand-teal">
              Two Very Real Problems
            </span>
          </h2>
          <p className="text-xl text-gray-500 leading-relaxed">
            PetCares is not a general pet services app. It was built specifically to answer two questions that thousands of pet owners across South India face — and could never find a reliable answer to.
          </p>
        </div>

        {/* Heritage Image */}
        <div className="max-w-5xl mx-auto mb-20 rounded-[2rem] overflow-hidden shadow-2xl border-4 border-gray-100 h-72 lg:h-[450px] relative group">
          <img src={require('../assets/images/trust.png')} alt="Community Trust" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60" />
          <div className="absolute bottom-6 left-6 text-white text-left">
            <h3 className="font-extrabold text-2xl mb-1">Built on Trust</h3>
            <p className="text-sm text-gray-200">Personally verified relationships with Coimbatore's best pet businesses.</p>
          </div>
        </div>

        {/* Problem cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {problems.map((p, i) => (
            <div key={i} className="rounded-3xl border border-gray-100 shadow-sm hover:shadow-lg transition-all p-8 group">
              <div className={`${p.bg} w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>{p.icon}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{p.title}</h3>
              <p className="text-gray-500 leading-relaxed text-[15px]">{p.body}</p>
            </div>
          ))}
        </div>

        {/* Values row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {values.map((v, i) => (
            <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:bg-white hover:shadow-md transition-all">
              <div className="mb-3">{v.icon}</div>
              <h4 className="font-bold text-gray-900 mb-1">{v.label}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>

        {/* Trust block */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h3 className="text-3xl font-extrabold text-white mb-4">Our Standard of Trust</h3>
            <p className="text-gray-300 leading-relaxed mb-6">
              Anyone can build a directory. We went further. Every business and doctor on PetCares has been individually verified —
              because when your pet's life depends on it, you deserve better than a random Google result.
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-teal/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="text-brand-teal" size={18} />
              </div>
              <p className="text-white font-semibold text-sm">
                Built by{' '}
                <a href="https://aitechies.in/" target="_blank" rel="noreferrer" className="text-brand-teal hover:underline">
                  AiTechies
                </a>{' '}
                · Coimbatore, Tamil Nadu
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {trustPoints.map((point, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                <CheckCircle2 className="text-brand-teal flex-shrink-0 mt-0.5" size={17} />
                <p className="text-gray-200 text-sm">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
